import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatIDR } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Trash2, ShieldAlert, Download, History, Database, AlertTriangle } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const CATEGORIES = [
  { id: "orders", label: "Order / Transaksi Harian", desc: "Seluruh pesanan masuk. Ikut menghapus tagihan pelanggan, tagihan supplier otomatis, cashback & laporan terkait.", period: true },
  { id: "expenses", label: "Pengeluaran", desc: "Catatan pengeluaran sesuai periode.", period: true },
  { id: "supplier_bills", label: "Tagihan Supplier", desc: "Tagihan & pembayaran supplier sesuai periode terbit.", period: true },
  { id: "customer_bills", label: "Tagihan Pelanggan", desc: "Order pelanggan yang BELUM LUNAS (piutang) sesuai periode. Ikut menghapus cashback & tagihan supplier otomatis dari order tsb.", period: true },
  { id: "kasbon", label: "Kasbon", desc: "Catatan kasbon karyawan/owner.", period: true },
  { id: "deposits", label: "Titipan Pelanggan", desc: "Seluruh saldo uang titipan pelanggan dari pembatalan order (riwayat pakai/refund ikut terhapus).", period: false },
  { id: "cashback_disbursements", label: "Riwayat Pencairan Cashback", desc: "Log pencairan cashback (audit saja — saldo kas/bank & status cashback order TIDAK berubah).", period: false },
  { id: "customers", label: "Data Pelanggan", desc: "Seluruh data pelanggan (tidak menghapus ordernya).", period: false },
  { id: "suppliers", label: "Data Supplier", desc: "Seluruh akun supplier (tidak menghapus tagihannya).", period: false },
];

export default function DataManagement() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [period, setPeriod, range, setRange] = usePersistentPeriod("data-management", "all");
  const [logs, setLogs] = useState([]);
  const [dialog, setDialog] = useState(null); // category object or {id:'all'}
  const [preview, setPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadLogs = () => { if (isOwner) api.get("/data/purge/logs").then((r) => setLogs(r.data)).catch(() => {}); };
  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, []);

  const usePeriod = dialog && dialog.id !== "all" && dialog.period;
  const qbody = () => ({ category: dialog.id, period: usePeriod ? period : "all", start: range.start, end: range.end });

  const openDialog = async (cat) => {
    setDialog(cat); setPassword(""); setConfirmText(""); setPreview(null); setSelectedIds([]);
    try {
      const body = { category: cat.id, period: cat.id !== "all" && cat.period ? period : "all", start: range.start, end: range.end };
      const { data } = await api.post("/data/purge/preview", body);
      setPreview(data);
      setSelectedIds((data.items || []).map((i) => i.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const refreshPreview = async () => {
    if (!dialog) return;
    try {
      const { data } = await api.post("/data/purge/preview", qbody());
      setPreview(data);
      setSelectedIds((data.items || []).map((i) => i.id));
    } catch { /* ignore */ }
  };
  useEffect(() => { if (dialog && usePeriod) refreshPreview(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);

  const items = preview?.items || [];
  const allChecked = items.length > 0 && selectedIds.length === items.length;
  const toggleItem = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelectedIds(allChecked ? [] : items.map((i) => i.id));

  const doDelete = async () => {
    if (!password) { toast.error("Masukkan password Anda"); return; }
    if (items.length > 0 && selectedIds.length === 0) { toast.error("Pilih minimal satu data"); return; }
    setBusy(true);
    try {
      const body = { ...qbody(), password, confirm_text: confirmText };
      if (dialog.id !== "all" && items.length > 0) body.ids = selectedIds;
      const { data } = await api.post("/data/purge", body);
      toast.success(`${data.affected} data dihapus`);
      setDialog(null); loadLogs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const exportBackup = async () => {
    try {
      const { data } = await api.get("/data/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `backup-lala-${today()}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup diunduh");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><Database className="h-5 w-5 text-[#C9A227]" /> Pengaturan Data &amp; Hapus Data</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Hapus data per kategori/periode dengan verifikasi password. Tindakan permanen — backup dulu.</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
          <Button variant="outline" className="gap-1.5" onClick={exportBackup} data-testid="btn-backup"><Download className="h-4 w-4" /> Backup</Button>
        </div>
      </div>

      <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-950/30 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-200">Penghapusan bersifat permanen dan dapat memengaruhi laporan keuangan (omzet, laba, piutang, saldo). Akun Owner/Admin, rekening bank, dan pengaturan sistem TIDAK ikut terhapus. Disarankan unduh Backup sebelum menghapus.</p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATEGORIES.map((c) => (
          <Card key={c.id} data-testid={`purge-card-${c.id}`} className="p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm">{c.label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{c.desc}</p>
              {c.period && <p className="text-[10px] text-primary mt-1">Mengikuti filter periode di atas</p>}
            </div>
            <Button variant="outline" size="sm" className="gap-1 border-rose-300 text-rose-600 hover:bg-rose-50 shrink-0" onClick={() => openDialog(c)} data-testid={`purge-btn-${c.id}`}>
              <Trash2 className="h-3.5 w-3.5" /> Hapus
            </Button>
          </Card>
        ))}
      </div>

      {isOwner && (
        <Card className="p-4 border-rose-300 bg-rose-50 dark:bg-rose-950/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div><p className="font-semibold text-sm text-rose-700 dark:text-rose-300">Hapus Seluruh Data Operasional</p>
              <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80 mt-0.5">Menghapus order, pengeluaran, tagihan supplier, kasbon, dan pelanggan. Akun login (termasuk supplier), rekening, dan pengaturan tetap aman.</p></div>
          </div>
          <Button variant="destructive" className="gap-1.5 shrink-0" onClick={() => openDialog({ id: "all", label: "Seluruh Data Operasional", period: false })} data-testid="purge-btn-all">
            <Trash2 className="h-4 w-4" /> Hapus Semua Data
          </Button>
        </Card>
      )}

      {isOwner && (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border"><History className="h-4 w-4 text-muted-foreground" /><p className="font-heading font-semibold text-sm">Riwayat Penghapusan</p></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Waktu</TableHead><TableHead>Pengguna</TableHead><TableHead>Kategori</TableHead><TableHead>Periode</TableHead><TableHead className="text-right">Terdampak</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8 text-sm">Belum ada riwayat penghapusan.</TableCell></TableRow>}
                {logs.map((lg) => (
                  <TableRow key={lg._id} data-testid={`log-row-${lg._id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{formatDate(lg.created_at)}</TableCell>
                    <TableCell className="text-xs">{lg.name || lg.username}</TableCell>
                    <TableCell className="text-xs">{lg.category}</TableCell>
                    <TableCell className="text-xs">{lg.period === "all" ? "Semua" : (lg.period === "custom" ? `${lg.start || "-"} s/d ${lg.end || "-"}` : lg.period)}</TableCell>
                    <TableCell className="text-right text-xs font-medium">{lg.affected}</TableCell>
                    <TableCell><Badge variant="outline" className={lg.status === "berhasil" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-rose-100 text-rose-800 border-rose-300"}>{lg.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-rose-600 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Konfirmasi Hapus</DialogTitle>
            <DialogDescription className="text-xs">Tindakan ini permanen dan dapat memengaruhi laporan keuangan.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 py-1" data-testid="purge-confirm-panel">
              <div className="rounded-lg border border-border p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Jenis Data</span><span className="font-semibold">{dialog.label}</span></div>
                {usePeriod && <div className="flex justify-between"><span className="text-muted-foreground">Periode</span><span className="font-medium">{period === "custom" ? `${range.start} s/d ${range.end}` : period}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Jumlah Terdampak</span>
                  <span className="font-bold text-rose-600" data-testid="purge-affected">
                    {preview ? (items.length > 0 ? `${selectedIds.length} dari ${preview.total} dipilih` : `${preview.total} data`) : "..."}
                  </span>
                </div>
              </div>
              {items.length > 0 && (
                <div className="rounded-lg border border-border" data-testid="purge-items-panel">
                  <label className="flex items-center gap-2 px-3 py-2 border-b border-border cursor-pointer text-xs font-medium hover:bg-accent/50" data-testid="purge-select-all">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-3.5 w-3.5 accent-[#0A1633]" />
                    Pilih Semua ({items.length})
                  </label>
                  <div className="max-h-44 overflow-y-auto divide-y divide-border">
                    {items.map((it) => (
                      <label key={it.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/50" data-testid={`purge-item-${it.id}`}>
                        <input type="checkbox" checked={selectedIds.includes(it.id)} onChange={() => toggleItem(it.id)} className="h-3.5 w-3.5 accent-[#0A1633] shrink-0" />
                        <span className="text-xs font-medium truncate flex-1">{it.label}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{it.sub}{it.amount ? ` · ${formatIDR(it.amount)}` : ""}</span>
                      </label>
                    ))}
                  </div>
                  {(dialog.id === "orders" || dialog.id === "customer_bills") && (
                    <p className="px-3 py-1.5 text-[10px] text-amber-700 dark:text-amber-300 border-t border-border">Tagihan supplier otomatis dari order yang dipilih ikut terhapus.</p>
                  )}
                </div>
              )}
              <p className="text-[11px] text-amber-700 dark:text-amber-300">⚠ Penghapusan memengaruhi omzet, laba, piutang, cashback, dan saldo kas/bank terkait.</p>
              {dialog.id === "all" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Ketik <b>HAPUS SEMUA DATA</b> untuk melanjutkan</Label>
                  <Input data-testid="purge-confirm-text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="HAPUS SEMUA DATA" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Password Akun Anda</Label>
                <Input data-testid="purge-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Masukkan password untuk verifikasi" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Batal</Button>
            <Button variant="destructive" onClick={doDelete} disabled={busy || !preview || (items.length > 0 ? selectedIds.length === 0 : preview.total === 0)} data-testid="purge-confirm-btn">
              {busy ? "Menghapus..." : "Konfirmasi Hapus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
